import React, { useState } from 'react'
import { apiService as api } from '../../services/api'
import { Link } from 'react-router-dom'
import { Container } from '../../components/ui/Container'
import { Button } from '../../components/ui/Button'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Heart, Mail, Lock, Eye, EyeOff } from 'lucide-react'
import { motion } from 'framer-motion'

const SignIn: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    
    try {
      const response = await api.login(formData.email, formData.password)
      
      if (response.success && response.data) {
        // Store token
        if (response.data.token) {
           localStorage.setItem('token', response.data.token);
        }
        // Redirect to dashboard
        window.location.href = '/dashboard';
      } else {
        alert(response.error || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error)
      alert('An unexpected error occurred')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  return (
    <div className="min-h-screen bg-theme-bg text-theme-primary flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <Container size="sm" className="w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full"
        >
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <Link to="/" className="inline-flex items-center justify-center space-x-2 mb-6">
              <div className="w-12 h-12 bg-gradient-to-r from-theme-primary to-theme-secondary rounded-xl flex items-center justify-center">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-theme-primary">BioTune</span>
            </Link>
            <h1 className="text-3xl font-bold text-theme-primary mb-2">Welcome back</h1>
            <p className="text-theme-secondary">Sign in to your account to continue your health journey</p>
          </div>

          {/* Sign In Card */}
          <Card className="border-theme-border shadow-xl hover:shadow-2xl transition-shadow duration-300 bg-theme-surface">
            <CardHeader className="text-center pb-6">
              <CardTitle className="text-2xl font-bold text-theme-primary">Sign In</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Email Field */}
                <div className="space-y-2">
                  <label htmlFor="email" className="block text-sm font-medium text-theme-secondary">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="space-y-2">
                  <label htmlFor="password" className="block text-sm font-medium text-theme-secondary">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-5 w-5 text-theme-muted" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-12 py-3 border border-theme-border rounded-lg focus:ring-2 focus:ring-theme-primary focus:border-transparent transition-all duration-200 bg-theme-surface text-theme-primary"
                      placeholder="Enter your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-3 text-theme-muted hover:text-theme-secondary transition-colors"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                {/* Remember Me & Forgot Password */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="rounded border-theme-border text-theme-primary focus:ring-theme-primary"
                    />
                    <span className="text-sm text-theme-secondary">Remember me</span>
                  </label>
                  <Link
                    to="/auth/forgot-password"
                    className="text-sm text-theme-primary hover:text-theme-secondary font-medium transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="w-full bg-gradient-to-r from-theme-primary to-theme-secondary hover:from-theme-primary-dark hover:to-theme-secondary-dark text-white font-medium py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <span className="flex items-center justify-center space-x-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Signing in...</span>
                    </span>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Sign Up Link */}
          <div className="text-center mt-6">
            <p className="text-theme-secondary">
              Don't have an account?{' '}
              <Link
                to="/auth/signup"
                className="text-theme-primary hover:text-theme-secondary font-medium transition-colors"
              >
                Sign up here
              </Link>
            </p>
          </div>

          {/* Demo Credentials */}
          <div className="mt-8 p-4 bg-theme-elevated rounded-lg border border-theme-border">
            <h3 className="text-sm font-medium text-theme-primary mb-2">Demo Credentials</h3>
            <div className="text-xs text-theme-secondary space-y-1">
              <p><strong>Email:</strong> demo@biotune.tech</p>
              <p><strong>Password:</strong> demo123</p>
            </div>
          </div>
        </motion.div>
      </Container>
    </div>
  )
}

export default SignIn
